import React, { useState, useRef, useEffect } from 'react';
import EventCard from './EventCard';
import Quiz from './Quiz';

const groupByCategoryCenturyAndTopic = (events) => {
  return events.reduce((groups, event) => {
    const category = event.category || 'Uncategorized';
    const century = `${Math.floor(event.year / 100) + 1}th Century`;
    const topic = event.topic || 'General';
    if (!groups[category]) groups[category] = [];
    groups[category].push({ ...event, century, topic });
    return groups;
  }, {});
};

const TimelineView = ({ events, view, gridMode, quizMode, resetTimeline }) => {
  const groupedEvents = groupByCategoryCenturyAndTopic(events);
  const categories = Object.keys(groupedEvents).sort();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCentury, setSelectedCentury] = useState('All');
  const [selectedTopic, setSelectedTopic] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 10;
  const containerRef = useRef(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizEvents, setQuizEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (quizMode) {
      setShowQuizModal(true);
      setSelectedCategory(null);
      setSelectedCentury('All');
      setSelectedTopic('All');
    }
  }, [quizMode]);

  useEffect(() => {
    if (view === 'horizontal' && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, view]);

  useEffect(() => {
    if (resetTimeline) {
      setSelectedCategory(null);
      setSelectedCentury('All');
      setSelectedTopic('All');
      setCurrentPage(1);
    }
  }, [resetTimeline]);

  const handleBack = () => {
    setSelectedCategory(null);
    setSelectedCentury('All');
    setSelectedTopic('All');
    setCurrentPage(1);
  };

  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setLoading(true);
    try {
      const response = await fetch('http://localhost:3001/api/generate-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `Give a concise, standalone explanation of the historical event titled: "${event.title}". Explain what it was, when it happened, why it mattered, and list 2–3 reliable online sources. Return the links as full URLs.`
        })
      });

      const data = await response.json();
      setAiSummary(data.summary || 'No summary available.');
    } catch (err) {
      setAiSummary('Failed to fetch AI summary.');
    }
    setLoading(false);
  };

  const getUniqueValues = (items, key) => Array.from(new Set(items.map(item => item[key]))).sort();

  const filteredEvents = selectedCategory
    ? groupedEvents[selectedCategory].filter(e =>
        (selectedCentury === 'All' || e.century === selectedCentury) &&
        (selectedTopic === 'All' || e.topic === selectedTopic)
      )
    : [];

  const paginatedEvents = filteredEvents.slice((currentPage - 1) * eventsPerPage, currentPage * eventsPerPage);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const getLayoutClasses = () => {
    if (gridMode) return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4';
    if (view === 'vertical') return 'flex flex-col items-center gap-4 px-4';
    return 'flex flex-row items-start gap-6 overflow-x-auto px-4 py-2';
  };

  const getCardWrapperClasses = () => {
    if (view === 'vertical') return 'flex items-stretch justify-center w-full';
    return 'flex items-stretch justify-center w-full h-[300px]';
  };

  const getCardInnerClasses = () => {
    return `${
      gridMode ? 'w-full' :
      view === 'horizontal' ? 'w-[300px]' :
      'w-full max-w-xl'
    } h-full transition-transform duration-300 ease-in-out hover:scale-[1.01]`;
  };

  return (
    <div className="px-4 pb-10 bg-background text-white min-h-screen">
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card p-6 rounded-xl shadow-lg max-w-xl w-full relative text-white">
            <button onClick={() => setSelectedEvent(null)} className="absolute top-2 right-2 text-white hover:text-accent">✕</button>
            <h2 className="text-xl font-bold mb-2">{selectedEvent.title}</h2>
            <p className="mb-4 text-sm text-gray-400">{selectedEvent.description}</p>
            {loading ? (
              <p className="text-accent">Loading summary...</p>
            ) : (
              <div className="text-sm space-y-2 mb-4">
                {aiSummary.split('\n').map((line, i) =>
                  line.trim().startsWith('http') ? (
                    <a
                      key={i}
                      href={line.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline block break-words"
                    >
                      {line.trim()}
                    </a>
                  ) : (
                    <p key={i}>{line}</p>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!quizMode && !selectedCategory ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 py-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className="bg-card hover:bg-accent hover:text-black text-white px-4 py-3 rounded-lg shadow-md transition duration-200"
            >
              {category}
            </button>
          ))}
        </div>
      ) : (
        selectedCategory && (
          <>
            <button onClick={handleBack} className="text-accent underline mb-4 inline-block">← Back to Categories</button>

            <div className="flex flex-wrap gap-4 items-center mb-6">
              <label>
                <span className="mr-2 text-sm">Century:</span>
                <select
                  value={selectedCentury}
                  onChange={(e) => setSelectedCentury(e.target.value)}
                  className="bg-card border border-gray-600 text-white rounded px-2 py-1"
                >
                  {['All', ...getUniqueValues(groupedEvents[selectedCategory], 'century')].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="mr-2 text-sm">Topic:</span>
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="bg-card border border-gray-600 text-white rounded px-2 py-1"
                >
                  {['All', ...getUniqueValues(groupedEvents[selectedCategory], 'topic')].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className={getLayoutClasses()} ref={containerRef}>
              {paginatedEvents.map((event, i) => (
                <div key={i} className={getCardWrapperClasses()}>
                  <div className={getCardInnerClasses()} onClick={() => handleEventClick(event)}>
                    <EventCard event={event} />
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-card text-white rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-card text-white rounded disabled:opacity-40"
                >
                  Next
                </button>

                {setQuizMode && !quizMode && (
  <div className="text-center mb-6">
    <button
      className="bg-blue-600 text-white px-4 py-2 rounded"
      onClick={() => setQuizMode(true)}
    >
      Start Quiz
    </button>
  </div>
)}

              </div>
            )}
          </>
        )
      )}
    </div>
  );
};

export default TimelineView;
